import { tr } from '@/lib/i18n-core';
import { useCallback, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Lock } from 'lucide-react';
import {
  CLAIM_FLAG,
  CLAIM_FLAG_LABEL,
  COMPLAINT_DURATION_UNIT,
  DISPOSITION,
  DISPOSITION_LABEL,
  FOLLOW_UP_DISCOUNT_MAX,
  FOLLOW_UP_DISCOUNT_MIN,
  FOLLOW_UP_VALID_DAYS_DEFAULT,
  FOLLOW_UP_VALID_DAYS_MAX,
  FOLLOW_UP_VALID_DAYS_MIN,
  ROS_FINDING,
  ROS_SYSTEMS,
  allNormalExcept,
  appendPhrase,
  requiredFormsFromDetails,
  suggestedDisposition,
  type ClaimFlag,
  type ComplaintDurationUnit,
  type Disposition,
  type EncounterFeeGate,
  type RosFinding,
  type RosSystem,
} from '@his/shared';
import { ApiError, del, get, post, put } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { closeGateFor, isFeeGate } from './fee-gate';
import { dateOnly, humanize } from '@/lib/utils';
import { ROUTE_LABELS } from './prescription-draft';
import { printDocument } from '@/lib/print';
import { Dialog } from '@/components/ui/layout';
import { Alert, Badge, Button, Card, CardContent, CardHeader, CardTitle, Checkbox, Field, Input, Popover, PopoverContent, PopoverTrigger, Select, Textarea } from '@/components/ui/primitives';
import { ReasonDialog } from '@/components/ui/reason-dialog';
import { useToast } from '@/components/ui/toast';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import { MissingFormsList } from './emr/EmrProgress';
import { QuickPickChips, useQuickPicks } from './emr/QuickPicks';
import { durationUnitLabel, rosFindingLabel, rosSystemLabel, severityLabel } from './emr/labels';

const ALLERGY_SEVERITIES = ['mild', 'moderate', 'severe'] as const;

export interface EmrEncounter {
  id: string;
  status: string;
  chiefComplaint: string | null;
  complaintDurationValue?: number | null;
  complaintDurationUnit?: string | null;
  disposition?: string | null;
  claimFlags?: string[];
  allergiesVerifiedAt?: string | null;
  followUpDate: string | null;
  followUpValidDays?: number | null;
  notes: Record<string, unknown>;
  appointment?: { payerId: string | null } | null;
  patient: {
    id: string;
    allergyStatus?: string | null;
    allergies: { id: string; substance: string; reaction: string | null; severity: string }[];
  };
}

function useEncounterSave(encounterId: string) {
  const toast = useToast();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (patch: Record<string, unknown>) => put(`/encounters/${encounterId}`, patch),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['encounter', encounterId] }),
    onError: (error) => toast.error(tr('opd', 'could_not_save'), describeError(error)),
  });
}

export function AllergyBanner({ encounter }: { encounter: EmrEncounter }) {
  if (encounter.patient.allergies.length > 0) return null;
  if (encounter.patient.allergyStatus === 'nka') {
    return <p className="mb-4 text-sm text-muted-foreground">{tr('opd', 'no_known_allergies')}{encounter.allergiesVerifiedAt ? tr('opd', 'checked_x', { allergiesVerifiedAt: dateOnly(encounter.allergiesVerifiedAt) }) : ''}.</p>;
  }
  return (
    <Alert variant="warning" className="mb-4">
      {tr('opd', 'allergies_not_recorded_ask_the_patient_then')}
    </Alert>
  );
}

export function ChiefComplaintBar({ encounter, readOnly }: { encounter: EmrEncounter; readOnly: boolean }) {
  const { mutate } = useEncounterSave(encounter.id);
  const picks = useQuickPicks(!readOnly, encounter.id);
  const [complaint, setComplaint] = useState(encounter.chiefComplaint ?? '');
  const [value, setValue] = useState(encounter.complaintDurationValue ? String(encounter.complaintDurationValue) : '');
  const [unit, setUnit] = useState<ComplaintDurationUnit>((encounter.complaintDurationUnit as ComplaintDurationUnit | null) ?? 'days');
  const latest = useRef({ complaint, value, unit });
  latest.current = { complaint, value, unit };

  const persist = useCallback(
    (next: { complaint?: string; value?: string; unit?: string }) => {
      const text = (next.complaint ?? latest.current.complaint).trim();
      const count = Number(next.value ?? latest.current.value);
      mutate({
        chiefComplaint: text || null,
        complaintDurationValue: Number.isInteger(count) && count > 0 ? count : null,
        complaintDurationUnit: Number.isInteger(count) && count > 0 ? (next.unit ?? latest.current.unit) : null,
      });
    },
    [mutate],
  );
  const pick = useCallback(
    (phrase: string) => {
      const next = appendPhrase(latest.current.complaint, phrase, ', ').slice(0, 400);
      setComplaint(next);
      persist({ complaint: next });
    },
    [persist],
  );

  return (
    <Card>
      <CardContent className="space-y-3 pt-4">
        <div className="grid gap-3 sm:grid-cols-[1fr_auto]">
          <Field label={tr('opd', 'chief_complaint')}>
            <Input value={complaint} maxLength={400} readOnly={readOnly} onChange={(e) => setComplaint(e.target.value)} onBlur={() => !readOnly && persist({})} />
          </Field>
          <div className="grid grid-cols-[auto_1fr] items-end gap-2">
            <Field label={tr('opd', 'for')}>
              <Input className="w-20" type="number" inputMode="numeric" min={1} max={999} value={value} readOnly={readOnly} onChange={(e) => setValue(e.target.value)} onBlur={() => !readOnly && persist({})} />
            </Field>
            <ChoiceButtons
              inline
              label={tr('opd', 'unit')}
              value={unit}
              disabled={readOnly}
              size="sm"
              className="mb-px"
              options={COMPLAINT_DURATION_UNIT.map((option) => ({ value: option, label: durationUnitLabel(option) }))}
              onChange={(next) => {
                setUnit(next);
                if (value) persist({ unit: next });
              }}
            />
          </div>
        </div>
        {!readOnly && <QuickPickChips field="chiefComplaint" learned={picks.learned.chiefComplaint} department={picks.department.chiefComplaint} onPick={pick} />}
      </CardContent>
    </Card>
  );
}

export function ReviewOfSystems({ encounter, readOnly }: { encounter: EmrEncounter; readOnly: boolean }) {
  const save = useEncounterSave(encounter.id);
  const { mutate } = save;
  const picks = useQuickPicks(!readOnly, encounter.id);
  const stored = (encounter.notes.rosFindings ?? {}) as Partial<Record<RosSystem, RosFinding>>;
  const [findings, setFindings] = useState<Partial<Record<RosSystem, RosFinding>>>(stored);
  const [text, setText] = useState(typeof encounter.notes.reviewOfSystems === 'string' ? encounter.notes.reviewOfSystems : '');
  const latest = useRef({ findings, text });
  latest.current = { findings, text };

  const persist = (nextFindings: Partial<Record<RosSystem, RosFinding>>, nextText = text) =>
    save.mutate({ notes: { rosFindings: nextFindings, reviewOfSystems: nextText } });
  const pick = useCallback(
    (phrase: string) => {
      const next = appendPhrase(latest.current.text, phrase).slice(0, 4000);
      setText(next);
      mutate({ notes: { rosFindings: latest.current.findings, reviewOfSystems: next } });
    },
    [mutate],
  );

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between">
        <CardTitle className="text-sm">{tr('opd', 'review_of_systems')}</CardTitle>
        {!readOnly && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              const abnormal = ROS_SYSTEMS.filter((system) => findings[system] === 'abnormal');
              const next = allNormalExcept(abnormal);
              setFindings(next);
              persist(next);
            }}
          >
            {tr('opd', 'all_normal_except_marked')}
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid gap-2 sm:grid-cols-2">
          {ROS_SYSTEMS.map((system) => (
            <div key={system} className="flex flex-wrap items-center justify-between gap-2 text-sm">
              <span>{rosSystemLabel(system)}</span>
              <div className="flex gap-1">
                {ROS_FINDING.map((finding) => (
                  <Button
                    key={finding}
                    size="sm"
                    variant={findings[system] === finding ? (finding === 'abnormal' ? 'destructive' : 'default') : 'outline'}
                    disabled={readOnly}
                    aria-pressed={findings[system] === finding}
                    onClick={() => {
                      const next = { ...findings, [system]: finding };
                      setFindings(next);
                      persist(next);
                    }}
                  >
                    {rosFindingLabel(finding)}
                  </Button>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div>
          <Field label={tr('opd', 'findings')}>
            <Textarea rows={2} maxLength={4000} value={text} readOnly={readOnly} onChange={(e) => setText(e.target.value)} onBlur={() => !readOnly && persist(findings, text)} />
          </Field>
          {!readOnly && <QuickPickChips field="reviewOfSystems" learned={picks.learned.reviewOfSystems} department={picks.department.reviewOfSystems} onPick={pick} className="mt-2" />}
        </div>
      </CardContent>
    </Card>
  );
}

export function AllergiesPanel({ encounter, readOnly }: { encounter: EmrEncounter; readOnly: boolean }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const save = useEncounterSave(encounter.id);
  const [substance, setSubstance] = useState('');
  const [reaction, setReaction] = useState('');
  const [severity, setSeverity] = useState<(typeof ALLERGY_SEVERITIES)[number]>('moderate');
  const [removing, setRemoving] = useState<{ id: string; substance: string } | null>(null);
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['encounter', encounter.id] });

  const remove = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => post(`/patients/${encounter.patient.id}/allergies/${id}/remove`, { reason }),
    onSuccess: () => {
      setRemoving(null);
      refresh();
    },
    onError: (error) => toast.error(tr('opd', 'could_not_remove_the_allergy'), describeError(error)),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">{tr('opd', 'allergies')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        {encounter.patient.allergies.length === 0 && (
          <p className="text-muted-foreground">{encounter.patient.allergyStatus === 'nka' ? tr('opd', 'no_known_allergies') : tr('opd', 'not_recorded')}</p>
        )}
        {encounter.patient.allergies.map((allergy) => (
          <div key={allergy.id} className="flex items-center justify-between gap-2">
            <span>
              <strong>{allergy.substance}</strong>
              {allergy.reaction ? ` — ${allergy.reaction}` : ''} <Badge variant={allergy.severity === 'severe' ? 'destructive' : 'warning'}>{severityLabel(allergy.severity)}</Badge>
            </span>
            {!readOnly && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setRemoving({ id: allergy.id, substance: allergy.substance })}
              >
                {tr('opd', 'remove')}
              </Button>
            )}
          </div>
        ))}
        {!readOnly && (
          <div className="grid gap-2 sm:grid-cols-[1fr_1fr_auto_auto]">
            <Input placeholder={tr('opd', 'substance')} value={substance} maxLength={160} onChange={(e) => setSubstance(e.target.value)} />
            <Input placeholder={tr('opd', 'reaction')} value={reaction} maxLength={240} onChange={(e) => setReaction(e.target.value)} />
            <ChoiceButtons
              inline
              label={tr('opd', 'emr_allergy_severity')}
              value={severity}
              options={ALLERGY_SEVERITIES.map((value) => ({ value, label: severityLabel(value) }))}
              onChange={(next) => setSeverity(next)}
            />
            <Button
              size="sm"
              disabled={!substance.trim()}
              onClick={() => {
                save.mutate({ allergies: [{ substance: substance.trim(), reaction: reaction.trim() || undefined, severity }] });
                setSubstance('');
                setReaction('');
              }}
            >
              {tr('opd', 'add')}
            </Button>
          </div>
        )}
        {!readOnly && (
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" disabled={encounter.patient.allergies.length > 0} onClick={() => save.mutate({ noKnownAllergies: true })}>
              {tr('opd', 'no_known_allergies')}
            </Button>
            <Button size="sm" variant="outline" onClick={() => save.mutate({ allergiesVerified: true })}>
              <Check className="h-4 w-4" /> {tr('opd', 'checked_with_the_patient_today')}
            </Button>
          </div>
        )}
      </CardContent>
      {removing && (
        <ReasonDialog
          title={tr('opd', 'remove_allergy_title', { substance: removing.substance })}
          description={tr('opd', 'remove_allergy_description')}
          minLength={3}
          maxLength={300}
          submitLabel={tr('opd', 'remove')}
          destructive
          loading={remove.isPending}
          onSubmit={(reason) => remove.mutate({ id: removing.id, reason })}
          onClose={() => setRemoving(null)}
        />
      )}
    </Card>
  );
}

interface CurrentMedicine {
  itemId: string;
  drugName: string;
  strength: string | null;
  dose: string;
  frequency: string;
  route: string;
  issuedAt: string;
  prescriber: string;
  stillTaking: boolean | null;
}

export function CurrentMedicinesPanel({ patientId, readOnly }: { patientId: string; readOnly: boolean }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const medicines = useQuery({ queryKey: ['current-medicines', patientId], queryFn: () => get<CurrentMedicine[]>(`/patients/${patientId}/current-medicines`) });
  const mark = useMutation({
    mutationFn: ({ itemId, stillTaking }: { itemId: string; stillTaking: boolean }) => put(`/prescription-items/${itemId}/still-taking`, { stillTaking }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['current-medicines', patientId] }),
    onError: (error) => toast.error(tr('opd', 'could_not_record_the_answer'), describeError(error)),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">{tr('opd', 'current_medicines')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        {medicines.data?.length === 0 && <p className="text-muted-foreground">{tr('opd', 'nothing_prescribed_here_in_the_last_year_is')}</p>}
        {medicines.data?.map((medicine) => (
          <div key={medicine.itemId} className="flex flex-wrap items-center justify-between gap-2">
            <span>
              <strong>{medicine.drugName}</strong>
              {medicine.strength ? ` ${medicine.strength}` : ''} — {medicine.dose} {medicine.frequency}, {(ROUTE_LABELS as Record<string, string>)[medicine.route] ?? humanize(medicine.route)}
              <span className="text-xs text-muted-foreground"> · {dateOnly(medicine.issuedAt)} · {medicine.prescriber}</span>
            </span>
            {!readOnly && (
              <div className="flex gap-1">
                <Button size="sm" variant={medicine.stillTaking === true ? 'default' : 'outline'} onClick={() => mark.mutate({ itemId: medicine.itemId, stillTaking: true })}>
                  {tr('opd', 'still_taking')}
                </Button>
                <Button size="sm" variant={medicine.stillTaking === false ? 'default' : 'outline'} onClick={() => mark.mutate({ itemId: medicine.itemId, stillTaking: false })}>
                  {tr('opd', 'stopped')}
                </Button>
              </div>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

interface Problem {
  id: string;
  icdCode: string;
  icdTitle: string;
  status: string;
  notedAt: string;
}

export function ProblemListPanel({ patientId, readOnly }: { patientId: string; readOnly: boolean }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const problems = useQuery({ queryKey: ['problems', patientId], queryFn: () => get<Problem[]>(`/patients/${patientId}/problems`) });
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['problems', patientId] });
  const setStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: 'active' | 'resolved' }) => put(`/problems/${id}`, { status }),
    onSuccess: refresh,
    onError: (error) => toast.error(tr('opd', 'could_not_update_the_problem'), describeError(error)),
  });
  const [erring, setErring] = useState<Problem | null>(null);
  const remove = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => del(`/problems/${id}`, { body: { reason } }),
    onSuccess: () => {
      setErring(null);
      refresh();
    },
    onError: (error) => toast.error(tr('opd', 'could_not_remove_the_problem'), describeError(error)),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">{tr('opd', 'problem_list')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        {problems.data?.length === 0 && <p className="text-muted-foreground">{tr('opd', 'no_long_term_conditions_recorded_add_one_from_a')}</p>}
        {problems.data?.map((problem) => (
          <div key={problem.id} className="flex flex-wrap items-center justify-between gap-2">
            <span className={problem.status === 'resolved' ? 'text-muted-foreground line-through' : ''}>
              <span className="font-mono text-xs text-primary">{problem.icdCode}</span> {problem.icdTitle}
            </span>
            {!readOnly && (
              <div className="flex gap-1">
                <Button size="sm" variant="outline" onClick={() => setStatus.mutate({ id: problem.id, status: problem.status === 'active' ? 'resolved' : 'active' })}>
                  {problem.status === 'active' ? tr('opd', 'resolved') : tr('opd', 'active_again')}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setErring(problem)}>
                  {tr('opd', 'entered_in_error')}
                </Button>
              </div>
            )}
          </div>
        ))}
      </CardContent>
      {erring !== null && (
        <ReasonDialog
          title={tr('opd', 'remove_problem_title', { problem: erring.icdTitle })}
          description={tr('opd', 'remove_problem_description')}
          minLength={3}
          maxLength={300}
          submitLabel={tr('opd', 'remove')}
          destructive
          loading={remove.isPending}
          onSubmit={(reason) => remove.mutate({ id: erring.id, reason })}
          onClose={() => setErring(null)}
        />
      )}
    </Card>
  );
}

export function ActiveProblemBadges({ patientId }: { patientId: string }) {
  const problems = useQuery({ queryKey: ['problems', patientId], queryFn: () => get<Problem[]>(`/patients/${patientId}/problems`) });
  const active = problems.data?.filter((problem) => problem.status === 'active') ?? [];
  if (active.length === 0) return null;
  return (
    <div className="mb-4 flex flex-wrap gap-1">
      {active.map((problem) => (
        <Badge key={problem.id} variant="secondary" title={problem.icdCode}>
          {problem.icdTitle}
        </Badge>
      ))}
    </div>
  );
}

export function ClaimFlagsPanel({ encounter, readOnly }: { encounter: EmrEncounter; readOnly: boolean }) {
  const save = useEncounterSave(encounter.id);
  const flags = new Set(encounter.claimFlags ?? []);
  if (!encounter.appointment?.payerId && flags.size === 0) return null;
  return (
    <Card className="mt-4">
      <CardHeader>
        <CardTitle className="text-sm">{tr('opd', 'for_the_insurance_claim')}</CardTitle>
      </CardHeader>
      <CardContent className="grid gap-2 sm:grid-cols-3">
        {CLAIM_FLAG.map((flag) => (
          <Checkbox
            key={flag}
            checked={flags.has(flag)}
            disabled={readOnly}
            onCheckedChange={(checked) => {
              const next = new Set(flags);
              if (checked) next.add(flag);
              else next.delete(flag);
              save.mutate({ claimFlags: [...next] as ClaimFlag[] });
            }}
            label={CLAIM_FLAG_LABEL[flag]}
          />
        ))}
      </CardContent>
    </Card>
  );
}

type FollowUpChoice = 'none' | 'free' | 'paid' | 'discount';

function wholeBetween(text: string, min: number, max: number): number | null {
  const value = Number(text);
  return text.trim() !== '' && Number.isInteger(value) && value >= min && value <= max ? value : null;
}

function refusedForms(error: unknown): string[] {
  return error instanceof ApiError ? requiredFormsFromDetails(error.details) : [];
}

function BlockedCloseButton({ missing, onOpenForm }: { missing: readonly string[]; onOpenForm?: (key: string) => void }) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button aria-disabled className="opacity-60 hover:opacity-80">
          <Lock className="h-4 w-4" aria-hidden /> {tr('opd', 'close_consultation')}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-3">
        <div className="space-y-2">
          <div>
            <p className="text-sm font-semibold">{tr('opd', 'emr_close_blocked_title')}</p>
            <p className="text-xs text-muted-foreground">{missing.length > 0 ? tr('opd', 'emr_close_blocked_hint') : tr('opd', 'record_a_diagnosis_first')}</p>
          </div>
          {missing.length > 0 && (
            <MissingFormsList
              missing={missing}
              onOpen={(key) => {
                setOpen(false);
                onOpenForm?.(key);
              }}
            />
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function CloseConsultationButton({
  encounter,
  hasDiagnosis,
  hint,
  feeGate,
  missingRequired = [],
  onOpenForm,
}: {
  encounter: EmrEncounter;
  hasDiagnosis: boolean;
  hint?: string | null;
  feeGate?: EncounterFeeGate | null;
  missingRequired?: readonly string[];
  onOpenForm?: (key: string) => void;
}) {
  const toast = useToast();
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [disposition, setDisposition] = useState<Disposition>((encounter.disposition as Disposition | null) ?? suggestedDisposition(encounter.followUpDate));
  const [feeOverrideReason, setFeeOverrideReason] = useState('');
  const [askingOverride, setAskingOverride] = useState(false);
  const [followUpChoice, setFollowUpChoice] = useState<FollowUpChoice>('none');
  const [discountPct, setDiscountPct] = useState('50');
  const [validDays, setValidDays] = useState(String(encounter.followUpValidDays ?? FOLLOW_UP_VALID_DAYS_DEFAULT));
  const gate = closeGateFor(feeGate, can('opd.fee.override'));
  const overriding = gate.mayOverride || askingOverride;
  const close = useMutation({
    mutationFn: () =>
      post(`/encounters/${encounter.id}/close`, {
        disposition,
        ...(overriding && feeOverrideReason.trim() ? { feeOverrideReason: feeOverrideReason.trim() } : {}),
        ...(followUpChoice !== 'none'
          ? {
              followUp: {
                kind: followUpChoice,
                validDays: validDaysValue,
                ...(followUpChoice === 'discount' ? { discountPct: discountValue } : {}),
              },
            }
          : {}),
      }),
    onSuccess: async () => {
      toast.success(tr('opd', 'consultation_closed'), tr('opd', 'the_appointment_is_marked_completed'));
      setOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['encounter', encounter.id] });
      void queryClient.invalidateQueries({ queryKey: ['queue'] });
      if (disposition === 'discharged_to_gp') {
        const letter = await get<{ html: string }>(`/encounters/${encounter.id}/print?letter=gp`);
        printDocument(letter.html);
      }
    },
    onError: (error) => {
      if (isFeeGate(error)) void queryClient.invalidateQueries({ queryKey: ['encounter', encounter.id] });
      if (isFeeGate(error) && can('opd.fee.override') && !feeOverrideReason.trim()) {
        setAskingOverride(true);
        return;
      }
      const refused = refusedForms(error);
      if (refused.length > 0) {
        setOpen(false);
        void queryClient.invalidateQueries({ queryKey: ['encounter', encounter.id] });
        void queryClient.invalidateQueries({ queryKey: ['emr-forms', encounter.id] });
        const labels = refused.map((key) => tr('opd', `emr_form_${key}`)).join(', ');
        toast.error(tr('opd', 'could_not_close_the_consultation'), `${describeError(error)}: ${labels}`);
        return;
      }
      toast.error(tr('opd', 'could_not_close_the_consultation'), describeError(error));
    },
  });
  const validDaysValue = wholeBetween(validDays, FOLLOW_UP_VALID_DAYS_MIN, FOLLOW_UP_VALID_DAYS_MAX);
  const discountValue = wholeBetween(discountPct, FOLLOW_UP_DISCOUNT_MIN, FOLLOW_UP_DISCOUNT_MAX);
  const followUpInvalid = followUpChoice !== 'none' && (validDaysValue === null || (followUpChoice === 'discount' && discountValue === null));
  const blocked = missingRequired.length > 0 || !hasDiagnosis;
  const blockedTitle = gate.needsPayment && !gate.mayOverride ? tr('opd', 'close_needs_the_fee_paid') : undefined;

  return (
    <>
      {blocked ? (
        <BlockedCloseButton missing={missingRequired} onOpenForm={onOpenForm} />
      ) : (
        <Button onClick={() => setOpen(true)} title={blockedTitle}>
          <Check className="h-4 w-4" /> {tr('opd', 'close_consultation')}
        </Button>
      )}
      {gate.needsPayment && !gate.mayOverride && can('invoice.read') && (
        <Link to={`/billing/statement?encounterId=${encounter.id}`} className="text-sm font-medium text-primary underline underline-offset-2">
          {tr('opd', 'collect_at_counter')}
        </Link>
      )}
      {open && (
        <Dialog
          open
          onClose={() => setOpen(false)}
          title={tr('opd', 'close_the_consultation')}
          footer={
            <>
              <Button variant="ghost" onClick={() => setOpen(false)}>
                {tr('opd', 'cancel')}
              </Button>
              <Button onClick={() => close.mutate()} loading={close.isPending} disabled={(overriding && feeOverrideReason.trim().length < 5) || followUpInvalid}>
                {overriding ? tr('opd', 'close_without_payment') : tr('opd', 'close_and_sign')}
              </Button>
            </>
          }
        >
          <Field label={tr('opd', 'how_does_the_visit_end')} required>
            <Select value={disposition} onChange={(e) => setDisposition(e.target.value as Disposition)}>
              {DISPOSITION.map((value) => (
                <option key={value} value={value}>
                  {DISPOSITION_LABEL[value]}
                </option>
              ))}
            </Select>
          </Field>
          <div className="mt-3 space-y-3">
            <Field label={tr('opd', 'follow_up_after_this_visit')} hint={followUpChoice === 'none' ? undefined : tr('opd', 'follow_up_decision_hint')}>
              <ChoiceButtons
                label={tr('opd', 'follow_up_after_this_visit')}
                value={followUpChoice}
                options={[
                  { value: 'none', label: tr('opd', 'follow_up_none') },
                  { value: 'free', label: tr('opd', 'follow_up_free') },
                  { value: 'paid', label: tr('opd', 'follow_up_paid') },
                  { value: 'discount', label: tr('opd', 'follow_up_discounted') },
                ]}
                onChange={(next) => setFollowUpChoice(next)}
              />
            </Field>
            {followUpChoice !== 'none' && (
              <div className="grid gap-3 sm:grid-cols-2">
                {followUpChoice === 'discount' && (
                  <Field label={tr('opd', 'follow_up_discount_percent')} required>
                    <Input
                      type="number"
                      min={FOLLOW_UP_DISCOUNT_MIN}
                      max={FOLLOW_UP_DISCOUNT_MAX}
                      value={discountPct}
                      onChange={(e) => setDiscountPct(e.target.value)}
                    />
                  </Field>
                )}
                <Field label={tr('opd', 'follow_up_valid_for_days')} required>
                  <Input
                    type="number"
                    min={FOLLOW_UP_VALID_DAYS_MIN}
                    max={FOLLOW_UP_VALID_DAYS_MAX}
                    value={validDays}
                    onChange={(e) => setValidDays(e.target.value)}
                  />
                </Field>
              </div>
            )}
          </div>
          {overriding && (
            <div className="mt-3 space-y-3">
              <Alert variant="warning" title={tr('opd', 'consultation_fee_unpaid')}>
                {tr('opd', 'say_why_the_consultation_closes_unpaid')}
              </Alert>
              <Field label={tr('opd', 'reason')} required>
                <Input value={feeOverrideReason} maxLength={300} onChange={(e) => setFeeOverrideReason(e.target.value)} />
              </Field>
            </div>
          )}
          {hint && <p className="mt-2 text-sm text-muted-foreground">{hint}</p>}
          {disposition === 'discharged_to_gp' && <p className="mt-2 text-sm text-muted-foreground">{tr('opd', 'a_letter_to_the_family_doctor_prints_after')}</p>}
        </Dialog>
      )}
    </>
  );
}
